<script>
import axios from "axios";
import moment from "moment";
import {
  map,
  isArray,
  isEmpty,
  head,
  split,
  filter,
  sortBy,
  join,
  padStart,
} from "lodash";
import CotizadorHabitaciones from "./CotizadorHabitaciones.vue";
import CotizadorCalendario from "./CotizadorCalendario.vue";

export default {
  props: {
    url: { type: String, required: true },
    circuito: { type: Object, required: true },
    mes: { type: String, default: '' },
  },
  components: {
    CotizadorHabitaciones,
    CotizadorCalendario,
  },
  data() {
    return {
      paramBases: [],
      paramSalida: null,
      paramCategoria: null,
      preloader: false,
      cotizacion: null,
    };
  },
  watch: {
    paramCategoria() {
      this.paramSalida = null;
    },
    paramSalida() {
      this.paramBases = [];
    },
  },
  computed: {
    codigo() {
      return `${this.circuito.EmpresaMayoristaId}-${this.circuito.CircuitoId}`;
    },
    categorias() {
      if (
        isArray(this.circuito.CircuitoParametro) &&
        !isEmpty(this.circuito.CircuitoParametro)
      ) {
        const items = [];
        const hoy = moment();
        const inicioMes = this.mes ? moment(this.mes) : null;
        const inicio = inicioMes && inicioMes > hoy ? inicioMes : hoy;
        this.circuito.CircuitoParametro.forEach((param) => {
          param.Categoria.forEach((categoria) => {
            const salidas = map(categoria.CPCSalida_, (salida) => {
              return moment(salida.CPCSalida_Fecha).toDate();
            }).filter((d) => inicio.diff(d) <= 0);
            if (!salidas.length) return;
            items.push({
              id: categoria.CircuitoCategoriaId,
              nombre: categoria.CircuitoCategoriaNombre,
              salidas,
            });
          });
        });
        return items;
      }
      return false;
    },
    habitaciones() {
      if (
        isArray(this.circuito.CircuitoParametro) &&
        !isEmpty(this.circuito.CircuitoParametro) &&
        isArray(this.circuito.CircuitoRangoEdad) &&
        !isEmpty(this.circuito.CircuitoRangoEdad)
      ) {
        let edades = [];
        this.circuito.CircuitoRangoEdad.forEach((edad) => {
          if (edad.CircuitoRangoEdadHabilitado) {
            const item = { id: edad.CircuitoRangoEdadNombre };

            switch (edad.CircuitoRangoEdadNombre) {
              case "A":
                item.nombre = "Adulto";
                break;
              case "AM":
                item.nombre = "Adulto Mayor";
                break;
              case "CH":
                item.nombre = "Menor";
                break;
              case "IN":
                item.nombre = "Infoa";
                break;
              default:
                item.nombre = "";
                break;
            }

            item.edades = [];
            for (
              let i = edad.CircuitoRangoEdadMinimo;
              i <= edad.CircuitoRangoEdadMaximo;
              i++
            ) {
              item.edades.push(i);
            }

            edades.push(item);
          }
        });

        edades = sortBy(edades, (edad) => {
          return head(edad.edades);
        });

        const adultosEdades = filter(edades, (edad) => {
          return edad.id !== "CH" && edad.id !== "IN";
        });

        const menoresEdades = filter(edades, (edad) => {
          return edad.id === "CH" || edad.id === "IN";
        });

        const habitaciones = [];
        const param = head(this.circuito.CircuitoParametro);

        if (param.CircuitoParametroHabSingle) {
          habitaciones.push({
            id: "SGL",
            nombre: "Single",
            adultos: 1,
            adultosEdades,
            menores:
              param.CParametroSingleMinMenores > 0 ||
              param.CParametroSingleMaxMenores > 0,
            menoresRangos: {
              min: param.CParametroSingleMinMenores,
              max: param.CParametroSingleMaxMenores,
            },
            menoresEdades,
          });
        }

        if (param.CircuitoParametroHabDoble) {
          habitaciones.push({
            id: "DBL",
            nombre: "Doble",
            adultos: 2,
            adultosEdades,
            menores:
              param.CParametroDobleMinMenores > 0 ||
              param.CParametroDobleMaxMenores > 0,
            menoresRangos: {
              min: param.CParametroDobleMinMenores,
              max: param.CParametroDobleMaxMenores,
            },
            menoresEdades,
          });
        }

        if (param.CircuitoParametroHabTriple) {
          habitaciones.push({
            id: "TPL",
            nombre: "Triple",
            adultos: 3,
            adultosEdades,
            menores:
              param.CParametroTripleMinMenores > 0 ||
              param.CParametroTripleMaxMenores > 0,
            menoresRangos: {
              min: param.CParametroTripleMinMenores,
              max: param.CParametroTripleMaxMenores,
            },
            menoresEdades,
          });
        }

        if (param.CircuitoParametroHabCuadruple) {
          habitaciones.push({
            id: "CDP",
            nombre: "Cuadruple",
            adultos: 4,
            adultosEdades,
            menores:
              param.CParametroCuadMinMenores > 0 ||
              param.CParametroCuadMaxMenores > 0,
            menoresRangos: {
              min: param.CParametroCuadMinMenores,
              max: param.CParametroCuadMaxMenores,
            },
            menoresEdades,
          });
        }

        return habitaciones;
      }
      return false;
    },
    params() {
      return {
        codigo: this.codigo,
        bases: join(
          map(this.paramBases, (base) => {
            return (
              padStart(base.adultos, 2, "0") +
              join(
                map(base.edades, (edad) => padStart(edad, 2, "0")),
                ""
              )
            );
          }),
          "-"
        ),
        salida: this.paramSalida
          ? moment(this.paramSalida).format("YYYY-MM-DD")
          : null,
        categoria: this.paramCategoria,
      };
    },
    isValid() {
      if (this.preloader) {
        return false;
      }

      if (!this.paramSalida) {
        return false;
      }

      if (!this.paramCategoria) {
        return false;
      }

      if (this.paramBases.length) {
        let valid = true;
        this.paramBases.forEach((base) => {
          if (base.adultos) {
            if (base.adultos + base.menores !== filter(base.edades).length) {
              valid = false;
            }
          } else {
            valid = false;
          }
        });
        return valid;
      } else {
        return false;
      }

      return true;
    },
    urlReserva() {
      if (this.isValid) {
        const query = new URLSearchParams();
        query.append("codigo", this.params.codigo);
        query.append("categoria", this.params.categoria);
        query.append("salida", this.params.salida);
        query.append("bases", this.params.bases);
        return `${this.url}/services/inventory/reservar?` + query.toString();
      }
      return false;
    },
    urlConsulta() {
      if (this.isValid) {
        const query = new URLSearchParams();
        query.append("salida", this.params.salida);
        query.append(
          "sgl",
          this.paramBases.filter((item) => item.base === "SGL").length
        );
        query.append(
          "dbl",
          this.paramBases.filter((item) => item.base === "DBL").length
        );
        query.append(
          "tpl",
          this.paramBases.filter((item) => item.base === "TPL").length
        );
        query.append("categoria", this.params.categoria);
        var habitaciones = this.paramBases.map((item, i) => ({
          base: item.base,
          numero: i + 1,
          pasajeros: item.edades.map((edad, j) => ({
            edad,
            numero: j + 1,
          })),
        }));
        query.append("habitaciones", JSON.stringify(habitaciones));

        return (
          `${this.url}/services/circuitos/consultar/${this.circuito.EmpresaMayoristaId}/${this.circuito.CircuitoId}` +
          "?" +
          query.toString()
        );
      }
      return false;
    },
  },
  methods: {
    async cotizar() {
      this.preloader = true;

      const url = `${this.url}/services/inventory/cotizar`;
      const response = await axios.get(url, { params: this.params });

      this.cotizacion = response.data;
      this.preloader = false;
    },
  },
};
</script>

<template>
  <div id="cotizador">
    <div class="panel panel-default">
      <div class="panel-heading">Cotizar</div>
      <div class="list-group">
        <div class="list-group-item" id="categorias">
          <label class="control-label" for="cotizador_categoria"
            >Categoria</label
          >
          <select
            class="form-control"
            v-model="paramCategoria"
            id="cotizador_categoria"
          >
            <option :value="null">Seleccionar</option>
            <option
              v-for="item in categorias"
              :key="item.id"
              :value="item.id"
              v-html="item.nombre"
            ></option>
          </select>
        </div>

        <div class="list-group-item" id="salidas" v-if="paramCategoria">
          <template v-for="categoria in categorias">
            <CotizadorCalendario
              v-if="categoria.id === paramCategoria"
              :key="categoria.id"
              v-model="paramSalida"
              :salidas="categoria.salidas"
            />
          </template>
        </div>
        <CotizadorHabitaciones
          v-model="paramBases"
          :params="habitaciones"
          v-if="paramSalida"
        />
      </div>
    </div>

    <progress-bar
      :value="65"
      striped
      active
      v-if="preloader"
      label
      label-text="Cotizando..."
    />
    <btn type="primary" block :disabled="!isValid" @click="cotizar" v-else>
      Cotizar
    </btn>

    <br />

    <template v-if="cotizacion">
      <template v-if="cotizacion.error">
        <Alert type="danger" class="animated shakeX">
          <p v-html="cotizacion.error_message"></p>
        </Alert>
        <Btn block type="info" :href="urlConsulta"
          ><i class="fa fa-envelope-open"></i> Consultar</Btn
        >
      </template>
      <template v-else>
        <Alert
          :type="cotizacion.cupos.valid ? 'success' : 'warning'"
          class="animated shakeX"
        >
          <h4
            v-html="
              cotizacion.cupos.valid
                ? 'Cupos disponibles'
                : 'No hay cupos disponibles'
            "
          ></h4>
          <p
            v-html="
              `Cupos por ${cotizacion.cupos.nombre} validos ${cotizacion.cupos.cupos}. Total seleccionados ${cotizacion.cupos.total}.`
            "
          ></p>
        </Alert>

        <div class="panel panel-default animated flipInX" id="desglose">
          <div
            class="panel-heading"
            v-html="'Detalle de la Cotizaci&oacute;n'"
          ></div>
          <table class="table">
            <thead>
              <tr>
                <th>Concepto</th>
                <th colspan="2">Monto</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(tarifa, key) in cotizacion.rules.tarifa.desglose"
                :key="key"
                :class="tarifa.class"
              >
                <th v-html="tarifa.nombre"></th>
                <td v-html="tarifa.moneda" class="small"></td>
                <td v-html="tarifa.monto" class="monto"></td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="success">
                <th v-html="cotizacion.rules.leyenda"></th>
                <th v-html="cotizacion.rules.tarifa.moneda" class="small"></th>
                <th v-html="cotizacion.rules.tarifa.total" class="monto"></th>
              </tr>
            </tfoot>
          </table>
        </div>
        <div class="panel panel-default" id="politicas">
          <div
            class="panel-heading"
            v-html="'Pol&iacute;ticas de Cancelaci&oacute;n'"
          ></div>
          <div class="list-group" v-if="cotizacion.politicas">
            <div
              v-for="politica in cotizacion.politicas"
              :key="politica.id"
              class="list-group-item"
            >
              <h4 class="list-group-item-heading" v-html="politica.nombre"></h4>
              <p class="list-group-item-text" v-html="politica.leyenda"></p>
            </div>
          </div>
        </div>

        <Btn block type="warning" v-if="cotizacion.reserva" :href="urlReserva"
          ><i class="fa fa-shopping-cart"></i> Reservar</Btn
        >
        <Btn block type="info" :href="urlConsulta" v-else
          ><i class="fa fa-envelope-open"></i> Consultar</Btn
        >
      </template>
    </template>
  </div>
</template>

<style lang="scss">
#cotizador {
  .panel {
    .panel-heading {
      text-align: center;
    }
    .list-group {
      .list-group-item {
        &#salidas {
          padding: 0;
          .vc-container {
            border: 0;
            margin: auto;
            box-shadow: none;
            border-radius: 0;
          }
        }
      }
    }

    &#politicas {
      > .list-group {
        > .list-group-item {
          > .list-group-item-heading {
            font-size: 16px;
          }
          > .list-group-item-text {
            font-size: 12px;
          }
        }
      }
    }
  }

  .progress {
    height: 34px;
    background: #ddd;
    margin-bottom: 0;
    > .progress-bar {
      font-size: 14px;
      padding: 7px 5px;
    }
  }
}
</style>
